import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import './LoanDetailPage.css';

const LoanDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Admin-selected interest rate and calculated values
  const [selectedInterestRate, setSelectedInterestRate] = useState(3); // Default 3%
  const [calculatedEMI, setCalculatedEMI] = useState(0);
  const [calculatedTotal, setCalculatedTotal] = useState(0);
  const [approvalData, setApprovalData] = useState({
    remarks: '',
    paymentMethod: 'cash',
    transactionId: '',
    preferredPaymentDate: 1
  });
  const [rejectionData, setRejectionData] = useState({
    reason: '',
    remarks: '',
    reapplyAfter: 6
  });
  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [showRejectionForm, setShowRejectionForm] = useState(false);

  useEffect(() => {
    fetchLoanDetails();
  }, [id]);

  useEffect(() => {
    if (loan && loan.loanAmount && loan.tenure) {
      calculateEMIValues();
    }
  }, [selectedInterestRate, loan?.loanAmount, loan?.tenure]);

  const fetchLoanDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${config.API_URL}/enhanced-loans/${id}`);
      setLoan(response.data);
      
      // Set initial interest rate from loan if available, otherwise use calculated rate
      if (response.data.interestRate) {
        setSelectedInterestRate(response.data.interestRate);
      } else if (response.data.customer?.creditScore) {
        // Calculate based on credit score
        const creditScore = response.data.customer.creditScore || response.data.creditScore?.creditScore || 300;
        if (creditScore >= 700) {
          setSelectedInterestRate(3);
        } else if (creditScore >= 500) {
          setSelectedInterestRate(4);
        } else if (creditScore >= 400) {
          setSelectedInterestRate(5);
        } else if (creditScore >= 350) {
          setSelectedInterestRate(6);
        } else {
          setSelectedInterestRate(7);
        }
      }
    } catch (error) {
      console.error('Error fetching loan details:', error);
      setError('Failed to load loan details');
    } finally {
      setLoading(false);
    }
  };

  // Calculate EMI using Reducing Balance Method
  const calculateEMIValues = () => {
    if (!loan || !loan.loanAmount || !loan.tenure) return;
    
    // EMI calculation is based on LOAN AMOUNT (₹5000), not on actual disbursed
    // Processing fee is deducted separately in backend, but user pays interest on full loan amount
    const principal = parseFloat(loan.loanAmount); // ₹5000 - Interest calculated on this
    const tenure = parseInt(loan.tenure, 10);
    
    // Validate inputs
    if (isNaN(principal) || principal <= 0) {
      console.error('Invalid loan amount:', loan.loanAmount);
      setCalculatedEMI(0);
      setCalculatedTotal(0);
      return;
    }
    
    if (isNaN(tenure) || tenure <= 0) {
      console.error('Invalid tenure:', loan.tenure);
      setCalculatedEMI(0);
      setCalculatedTotal(0);
      return;
    }
    
    if (isNaN(selectedInterestRate) || selectedInterestRate < 0) {
      console.error('Invalid interest rate:', selectedInterestRate);
      setCalculatedEMI(0);
      setCalculatedTotal(0);
      return;
    }
    
    const monthlyRate = selectedInterestRate / 100;
    
    if (monthlyRate === 0) {
      const emi = principal / tenure;
      setCalculatedEMI(Math.round(emi * 100) / 100);
      setCalculatedTotal(Math.round(emi * tenure * 100) / 100);
      return;
    }
    
    // EMI Formula: P × r × (1+r)^n / [(1+r)^n - 1]
    // Calculate based on LOAN AMOUNT (₹5000), processing fee is separate
    const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, tenure)) / (Math.pow(1 + monthlyRate, tenure) - 1);
    const total = emi * tenure;
    
    // Validate results
    if (isNaN(emi) || isNaN(total) || emi <= 0 || total <= 0) {
      console.error('Invalid EMI calculation result:', { emi, total, principal, monthlyRate, tenure });
      setCalculatedEMI(0);
      setCalculatedTotal(0);
      return;
    }
    
    setCalculatedEMI(Math.round(emi * 100) / 100);
    setCalculatedTotal(Math.round(total * 100) / 100);
  };

  const handleInterestRateChange = (rate) => {
    setSelectedInterestRate(rate);
  };

  const handleApprove = async () => {
    if (!window.confirm(`Are you sure you want to approve this loan with ${selectedInterestRate}% interest rate?\n\nMonthly EMI: ₹${calculatedEMI.toLocaleString()}\nTotal Amount: ₹${calculatedTotal.toLocaleString()}`)) {
      return;
    }
    setShowApprovalForm(true);
  };

  const handleReject = async () => {
    setShowRejectionForm(true);
  };

  const submitApproval = async (e) => {
    e.preventDefault();
    
    // Validate calculated values before submission
    if (!calculatedEMI || calculatedEMI <= 0 || isNaN(calculatedEMI)) {
      alert('Invalid EMI calculation. Please check the interest rate and loan details.');
      return;
    }
    
    if (!calculatedTotal || calculatedTotal <= 0 || isNaN(calculatedTotal)) {
      alert('Invalid total amount calculation. Please check the interest rate and loan details.');
      return;
    }
    
    if (!selectedInterestRate || selectedInterestRate <= 0 || isNaN(selectedInterestRate)) {
      alert('Please select a valid interest rate.');
      return;
    }
    
    try {
      setProcessing(true);
      
      const formData = new FormData();
      formData.append('action', 'approve');
      formData.append('interestRate', selectedInterestRate.toString());
      formData.append('monthlyEMI', calculatedEMI.toString());
      formData.append('totalAmount', calculatedTotal.toString());
      formData.append('loanTenure', loan.tenure.toString());
      formData.append('remarks', approvalData.remarks);
      formData.append('paymentMethod', approvalData.paymentMethod);
      formData.append('transactionId', approvalData.transactionId);
      formData.append('preferredPaymentDate', approvalData.preferredPaymentDate.toString());
      
      const response = await axios.post(
        `${config.API_URL}/enhanced-loans/${id}/admin-action`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      alert('Loan approved successfully!');
      navigate('/loan-management');
    } catch (error) {
      console.error('Error approving loan:', error);
      alert('Failed to approve loan: ' + (error.response?.data?.message || error.message));
    } finally {
      setProcessing(false);
      setShowApprovalForm(false);
    }
  };

  const submitRejection = async (e) => {
    e.preventDefault();
    try {
      setProcessing(true);
      
      const response = await axios.post(
        `${config.API_URL}/enhanced-loans/${id}/admin-action`,
        {
          action: 'reject',
          reason: rejectionData.reason,
          remarks: rejectionData.remarks,
          reapplyAfter: rejectionData.reapplyAfter
        }
      );
      
      alert('Loan rejected successfully!');
      navigate('/loan-management');
    } catch (error) {
      console.error('Error rejecting loan:', error);
      alert('Failed to reject loan: ' + (error.response?.data?.message || error.message));
    } finally {
      setProcessing(false);
      setShowRejectionForm(false);
    }
  };


  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <span className="badge bg-warning text-dark">Pending</span>,
      approved: <span className="badge bg-success">Approved</span>,
      active: <span className="badge bg-primary">Active</span>,
      completed: <span className="badge bg-secondary">Completed</span>,
      rejected: <span className="badge bg-danger">Rejected</span>
    };
    return badges[status] || <span className="badge bg-secondary">{status}</span>;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading loan details...</p>
        </div>
      </div>
    );
  }

  if (error || !loan) {
    return (
      <div className="alert alert-danger">
        <h4>Error</h4>
        <p>{error || 'Loan not found'}</p>
        <button className="btn btn-primary" onClick={() => navigate('/loan-management')}>
          Back to Loan Management
        </button>
      </div>
    );
  }

  return (
    <div className="loan-detail-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <button className="btn btn-outline-secondary mb-3" onClick={() => navigate('/loan-management')}>
            <i className="bi bi-arrow-left me-2"></i>Back to Loan Management
          </button>
          <h2 className="mb-0">
            <i className="bi bi-bank me-2"></i>Loan Application Details
          </h2>
          <p className="text-muted mb-0">Application ID: {loan._id?.toString().slice(-8).toUpperCase()}</p>
        </div>
        <div>
          {getStatusBadge(loan.status)}
        </div>
      </div>

      <div className="row">
        {/* Left Column - Customer & Loan Info */}
        <div className="col-lg-8">
          {/* Customer Information Card */}
          <div className="card mb-4">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">
                <i className="bi bi-person-circle me-2"></i>Customer Information
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="text-muted small">Full Name</label>
                    <p className="mb-0 fw-bold fs-5">{loan.customer?.name || loan.name || 'N/A'}</p>
                  </div>
                  <div className="mb-3">
                    <label className="text-muted small">Phone Number</label>
                    <p className="mb-0">
                      <a href={`tel:${loan.customer?.phone || loan.phone}`} className="text-decoration-none">
                        {loan.customer?.phone || loan.phone || 'N/A'}
                      </a>
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="text-muted small">Email</label>
                    <p className="mb-0">{loan.customer?.email || 'N/A'}</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="text-muted small">Address</label>
                    <p className="mb-0">{loan.customer?.address || loan.address || 'N/A'}</p>
                  </div>
                  <div className="mb-3">
                    <label className="text-muted small">Credit Score</label>
                    <p className="mb-0">
                      <span className="badge bg-info fs-6">{loan.creditScore?.creditScore || loan.customer?.creditScore || 'N/A'}</span>
                      <span className="badge bg-success ms-2">
                        {loan.creditScore?.creditGrade || loan.customer?.creditGrade || 'N/A'}
                      </span>
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="text-muted small">Application Date</label>
                    <p className="mb-0">{formatDate(loan.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Location Information - Show if location exists OR address exists */}
              {((loan.location && loan.location.latitude && loan.location.longitude) || loan.customer?.address || loan.address) && (
                <div className="mt-4">
                  <hr />
                  <h6 className="mb-3">
                    <i className="bi bi-geo-alt-fill me-2 text-danger"></i>Application Location
                  </h6>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="text-muted small">Address</label>
                      <p className="mb-2 fw-bold">{loan.location?.address || loan.customer?.address || loan.address || 'N/A'}</p>
                      {loan.location && loan.location.latitude && loan.location.longitude ? (
                        <>
                          <label className="text-muted small">Coordinates</label>
                          <p className="mb-3">
                            <code>{loan.location.latitude.toFixed(6)}, {loan.location.longitude.toFixed(6)}</code>
                          </p>
                          <div className="d-flex flex-wrap gap-2 mb-3">
                        <a
                          href={`https://www.google.com/maps?q=${loan.location.latitude},${loan.location.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-primary"
                        >
                          <i className="bi bi-map me-1"></i>View on Maps
                        </a>
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${loan.location.latitude},${loan.location.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-success"
                        >
                          <i className="bi bi-signpost-split me-1"></i>Get Directions
                        </a>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${loan.location.latitude},${loan.location.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-info"
                        >
                          <i className="bi bi-geo-alt me-1"></i>Search Location
                        </a>
                          </div>
                          <div className="alert alert-success mt-3 mb-0">
                            <small>
                              <i className="bi bi-info-circle me-1"></i>
                              <strong>For Team Visit:</strong> Click "Get Directions" to open Google Maps with turn-by-turn navigation to this exact location.
                            </small>
                          </div>
                        </>
                      ) : (
                        <div className="alert alert-warning mt-3 mb-0">
                          <small>
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            GPS coordinates not available. Only address is shown.
                          </small>
                        </div>
                      )}
                    </div>
                    {loan.location && loan.location.latitude && loan.location.longitude && (
                      <div className="col-md-6">
                        <label className="text-muted small">Map View</label>
                        <div className="location-map-container">
                          <iframe
                            width="100%"
                            height="250"
                            frameBorder="0"
                            style={{ border: 0, borderRadius: '8px' }}
                            src={`https://www.google.com/maps?q=${loan.location.latitude},${loan.location.longitude}&output=embed&zoom=15`}
                            allowFullScreen
                            title="Loan Application Location"
                          ></iframe>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Loan Information Card */}
          <div className="card mb-4">
            <div className="card-header bg-info text-white">
              <h5 className="mb-0">
                <i className="bi bi-bank me-2"></i>Loan Information
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="text-muted small">Loan Type</label>
                    <p className="mb-0 fw-bold">{loan.loanType || 'N/A'}</p>
                  </div>
                  <div className="mb-3">
                    <label className="text-muted small">Purpose</label>
                    <p className="mb-0">{loan.loanPurpose || loan.purpose || 'N/A'}</p>
                  </div>
                  <div className="mb-3">
                    <label className="text-muted small">Requested Amount</label>
                    <p className="mb-0 fw-bold fs-5 text-primary">{formatCurrency(loan.loanAmount)}</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="text-muted small">Tenure</label>
                    <p className="mb-0 fw-bold">{loan.tenure || 'N/A'} months</p>
                  </div>
                  <div className="mb-3">
                    <label className="text-muted small">Risk Level</label>
                    <p className="mb-0">
                      <span className={`badge ${
                        loan.riskLevel === 'low' ? 'bg-success' :
                        loan.riskLevel === 'medium' ? 'bg-warning' : 'bg-danger'
                      }`}>
                        {loan.riskLevel || 'N/A'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Schedule / History */}
          {Array.isArray(loan.payments) && loan.payments.length > 0 && (
            <div className="card mb-4">
              <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="bi bi-calendar3 me-2"></i>Payment Schedule &amp; History
                </h5>
                <span className="badge bg-light text-dark">
                  {loan.payments.filter((p) => p.status === 'completed').length}/{loan.payments.length} paid
                </span>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Due Date</th>
                        <th>Paid On</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Status</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...loan.payments]
                        .sort((a, b) => (a.installmentNumber || 0) - (b.installmentNumber || 0))
                        .map((p, idx) => (
                          <tr
                            key={idx}
                            className={
                              p.status === 'completed'
                                ? ''
                                : p.status === 'overdue'
                                  ? 'table-danger'
                                  : 'table-warning'
                            }
                          >
                            <td>{p.installmentNumber || idx + 1}</td>
                            <td>{p.dueDate ? formatDate(p.dueDate) : '—'}</td>
                            <td>
                              {p.status === 'completed' && p.date ? formatDate(p.date) : '—'}
                            </td>
                            <td className="fw-semibold">{formatCurrency(p.amount)}</td>
                            <td>{p.paymentMethod || '—'}</td>
                            <td>
                              <span
                                className={`badge ${
                                  p.status === 'completed'
                                    ? 'bg-success'
                                    : p.status === 'overdue'
                                      ? 'bg-danger'
                                      : 'bg-warning text-dark'
                                }`}
                              >
                                {p.status || 'pending'}
                              </span>
                            </td>
                            <td className="small text-muted" style={{ maxWidth: 180 }}>
                              {p.notes || p.receiptNumber || '—'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {loan.status === 'active' && (
                  <div className="p-3 border-top bg-light small">
                    EMI collect / settle:{' '}
                    <a href="/loan-emi-dashboard" className="alert-link">
                      Loan EMI Dashboard
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Collateral & Witness Information */}
          {(loan.collateral || loan.witness) && (
            <div className="card mb-4">
              <div className="card-header bg-light">
                <h6 className="mb-0">
                  <i className="bi bi-shield-check me-2"></i>Additional Information
                </h6>
              </div>
              <div className="card-body">
                <div className="row">
                  {loan.collateral && loan.collateral.type !== 'none' && (
                    <div className="col-md-6">
                      <h6>Collateral</h6>
                      <p><strong>Type:</strong> {loan.collateral.type}</p>
                      {loan.collateral.description && (
                        <p><strong>Description:</strong> {loan.collateral.description}</p>
                      )}
                      {loan.collateral.value && (
                        <p><strong>Value:</strong> {formatCurrency(loan.collateral.value)}</p>
                      )}
                    </div>
                  )}
                  {loan.witness && (loan.witness.witnessName || loan.witness.witnessPhone) && (
                    <div className="col-md-6">
                      <h6>Witness</h6>
                      <div className="alert alert-info mb-2">
                        <i className="bi bi-info-circle me-2"></i>
                        <strong>Note:</strong> This loan has been issued on the witness of the person mentioned below (Just as a Formality).
                      </div>
                      {loan.witness.witnessName && (
                        <p><strong>Witness Name:</strong> {loan.witness.witnessName}</p>
                      )}
                      {loan.witness.witnessPhone && (
                        <p><strong>Witness Phone:</strong> {loan.witness.witnessPhone}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Documents & Agreements Section - Always show */}
          <div className="card mb-4">
            <div className="card-header bg-info text-white">
              <h5 className="mb-0">
                <i className="bi bi-folder me-2"></i>Documents & Agreements
              </h5>
            </div>
            <div className="card-body">
                {/* Loan Agreement */}
                {loan.loanAgreement && loan.loanAgreement.path && (
                  <div className="mb-3">
                    <h6>
                      <i className="bi bi-file-earmark-pdf me-2 text-danger"></i>Loan Agreement
                    </h6>
                    <div className="d-flex align-items-center flex-wrap">
                      <span className="me-3 mb-2">
                        {loan.loanAgreement.agreementNumber || 'N/A'} - Generated on {formatDate(loan.loanAgreement.generatedAt)}
                      </span>
                      <div className="mb-2">
                        <a
                          href={loan.loanAgreement.url || `/uploads/loans/agreements/${loan.loanAgreement.filename}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          <i className="bi bi-download me-1"></i>Download
                        </a>
                        <a
                          href={loan.loanAgreement.url || `/uploads/loans/agreements/${loan.loanAgreement.filename}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-info ms-2"
                        >
                          <i className="bi bi-eye me-1"></i>View
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {/* Disbursement Proof */}
                {loan.disbursementProof && loan.disbursementProof.path && (
                  <div className="mb-3">
                    <h6>
                      <i className="bi bi-receipt me-2 text-success"></i>Disbursement Proof
                    </h6>
                    <div className="d-flex align-items-center flex-wrap">
                      <span className="me-3 mb-2">
                        Payment Method: {loan.disbursementProof.paymentMethod || 'N/A'}
                        {loan.disbursementProof.transactionId && ` - Transaction ID: ${loan.disbursementProof.transactionId}`}
                        {loan.disbursementProof.uploadedAt && ` - Uploaded: ${formatDate(loan.disbursementProof.uploadedAt)}`}
                      </span>
                      <div className="mb-2">
                        <a
                          href={`/uploads/${loan.disbursementProof.path.replace(/^uploads\//, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          <i className="bi bi-download me-1"></i>Download
                        </a>
                        <a
                          href={`/uploads/${loan.disbursementProof.path.replace(/^uploads\//, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-info ms-2"
                        >
                          <i className="bi bi-eye me-1"></i>View
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {/* Customer Signature - View Only */}
                {loan.customerSignature && loan.customerSignature.path && (
                  <div className="mb-3">
                    <h6>
                      <i className="bi bi-pen me-2 text-primary"></i>Customer Signature
                    </h6>
                    <div className="d-flex align-items-center flex-wrap">
                      <span className="me-3 mb-2">
                        {loan.customerSignature.originalName || 'Signature'} - Uploaded: {formatDate(loan.customerSignature.uploadedAt)}
                      </span>
                      <div className="mb-2">
                        <a
                          href={`/uploads/${loan.customerSignature.path.replace(/^uploads\//, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          <i className="bi bi-download me-1"></i>Download
                        </a>
                        <a
                          href={`/uploads/${loan.customerSignature.path.replace(/^uploads\//, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-info ms-2"
                        >
                          <i className="bi bi-eye me-1"></i>View
                        </a>
                      </div>
                    </div>
                    <small className="text-muted d-block mt-2">
                      <i className="bi bi-info-circle me-1"></i>
                      To upload or manage signature, please use the "Manage Documents" option from Loan Management page.
                    </small>
                  </div>
                )}

                {/* Other Documents - Filter out loan_agreement and signature if already shown */}
                {loan.documents && loan.documents.length > 0 && (() => {
                  const filteredDocs = loan.documents.filter(doc => 
                    doc.type !== 'loan_agreement' && 
                    !(doc.type === 'signature' && loan.customerSignature && loan.customerSignature.path)
                  );
                  return filteredDocs.length > 0 ? (
                    <div className="mb-3">
                      <h6>
                        <i className="bi bi-file-earmark me-2"></i>Other Documents ({filteredDocs.length})
                      </h6>
                      <div className="list-group">
                        {filteredDocs.map((doc, index) => (
                        <div key={index} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div className="mb-2">
                              <strong>{doc.type.replace(/_/g, ' ').toUpperCase()}</strong>
                              {doc.name && <div className="small text-muted">{doc.name}</div>}
                              {doc.description && <div className="small">{doc.description}</div>}
                              {doc.uploadedAt && (
                                <div className="small text-muted">Uploaded: {formatDate(doc.uploadedAt)}</div>
                              )}
                            </div>
                            <div className="mb-2">
                              {doc.url && (
                                <>
                                  <a
                                    href={doc.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary"
                                  >
                                    <i className="bi bi-download me-1"></i>Download
                                  </a>
                                  <a
                                    href={doc.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-info ms-2"
                                  >
                                    <i className="bi bi-eye me-1"></i>View
                                  </a>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      </div>
                    </div>
                  ) : null;
                })()}

                {!loan.loanAgreement && !loan.disbursementProof && !loan.customerSignature && (!loan.documents || loan.documents.length === 0) && (
                  <div className="text-muted text-center py-4">
                    <i className="bi bi-inbox me-2" style={{fontSize: '2rem'}}></i>
                    <div className="mt-2">No documents available yet</div>
                    <small className="text-muted">Documents will appear here once uploaded</small>
                  </div>
                )}
              </div>
            </div>
        </div>

        {/* Right Column - Admin Actions & Calculation */}
        <div className="col-lg-4">
          {/* Interest Rate Selection & EMI Calculation */}
          {loan.status === 'pending' && (
            <div className="card mb-4 border-primary">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="bi bi-calculator me-2"></i>Set Interest Rate & Calculate
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-bold">Select Interest Rate (Per Month)</label>
                  <div className="btn-group w-100" role="group">
                    <input
                      type="radio"
                      className="btn-check"
                      name="interestRate"
                      id="rate3"
                      value="3"
                      checked={selectedInterestRate === 3}
                      onChange={() => handleInterestRateChange(3)}
                    />
                    <label className="btn btn-outline-primary" htmlFor="rate3">3%</label>

                    <input
                      type="radio"
                      className="btn-check"
                      name="interestRate"
                      id="rate4"
                      value="4"
                      checked={selectedInterestRate === 4}
                      onChange={() => handleInterestRateChange(4)}
                    />
                    <label className="btn btn-outline-primary" htmlFor="rate4">4%</label>

                    <input
                      type="radio"
                      className="btn-check"
                      name="interestRate"
                      id="rate5"
                      value="5"
                      checked={selectedInterestRate === 5}
                      onChange={() => handleInterestRateChange(5)}
                    />
                    <label className="btn btn-outline-primary" htmlFor="rate5">5%</label>

                    <input
                      type="radio"
                      className="btn-check"
                      name="interestRate"
                      id="rate6"
                      value="6"
                      checked={selectedInterestRate === 6}
                      onChange={() => handleInterestRateChange(6)}
                    />
                    <label className="btn btn-outline-primary" htmlFor="rate6">6%</label>

                    <input
                      type="radio"
                      className="btn-check"
                      name="interestRate"
                      id="rate7"
                      value="7"
                      checked={selectedInterestRate === 7}
                      onChange={() => handleInterestRateChange(7)}
                    />
                    <label className="btn btn-outline-primary" htmlFor="rate7">7%</label>
                  </div>
                </div>

                <div className="calculation-result p-3 bg-light rounded">
                  <h6 className="text-muted mb-3">Calculation Preview</h6>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Loan Amount (Disbursed):</span>
                    <strong>{formatCurrency(loan.loanAmount)}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted small">Processing Fee (5%):</span>
                    <strong className="text-warning small">- {formatCurrency(Math.round(loan.loanAmount * 0.05 * 100) / 100)}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted small">Net Amount Received:</span>
                    <strong className="text-success small">{formatCurrency(Math.round((loan.loanAmount - loan.loanAmount * 0.05) * 100) / 100)}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Interest Rate:</span>
                    <strong>{selectedInterestRate}% per month</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Tenure:</span>
                    <strong>{loan.tenure} months</strong>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between mb-2">
                    <span>Monthly EMI:</span>
                    <strong className="text-success fs-5">{formatCurrency(calculatedEMI)}</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Total Amount:</span>
                    <strong className="text-primary fs-5">{formatCurrency(calculatedTotal)}</strong>
                  </div>
                  <div className="d-flex justify-content-between mt-2">
                    <span className="text-muted small">Total Interest:</span>
                    <strong className="text-muted small">{formatCurrency(calculatedTotal - parseFloat(loan.loanAmount))}</strong>
                  </div>
                  <div className="alert alert-info mt-3 mb-0">
                    <small>
                      <i className="bi bi-info-circle me-1"></i>
                      <strong>Note:</strong> Interest is calculated on full loan amount (₹{loan.loanAmount.toLocaleString()}). Processing fee (5%) is deducted separately at disbursement.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Approval/Rejection Actions */}
          {loan.status === 'pending' && (
            <div className="card mb-4">
              <div className="card-header bg-success text-white">
                <h5 className="mb-0">
                  <i className="bi bi-check-circle me-2"></i>Admin Actions
                </h5>
              </div>
              <div className="card-body">
                <div className="d-grid gap-2">
                  <button
                    className="btn btn-success btn-lg"
                    onClick={handleApprove}
                    disabled={processing}
                  >
                    <i className="bi bi-check-circle me-2"></i>Approve Loan
                  </button>
                  <button
                    className="btn btn-danger btn-lg"
                    onClick={handleReject}
                    disabled={processing}
                  >
                    <i className="bi bi-x-circle me-2"></i>Reject Loan
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Current Loan Details (if approved/active) */}
          {loan.status !== 'pending' && (
            <div className="card mb-4">
              <div className="card-header bg-secondary text-white">
                <h5 className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>Current Loan Details
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="text-muted small">Interest Rate</label>
                  <p className="mb-0 fw-bold">{loan.interestRate || 'N/A'}% per month</p>
                </div>
                <div className="mb-3">
                  <label className="text-muted small">Monthly EMI</label>
                  <p className="mb-0 fw-bold text-success">{formatCurrency(loan.monthlyInstallment)}</p>
                </div>
                <div className="mb-3">
                  <label className="text-muted small">Total Amount</label>
                  <p className="mb-0 fw-bold text-primary">{formatCurrency(loan.totalAmount)}</p>
                </div>
                {loan.approvedDate && (
                  <div className="mb-0">
                    <label className="text-muted small">Approved Date</label>
                    <p className="mb-0">{formatDate(loan.approvedDate)}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Approval Modal */}
      {showApprovalForm && (
        <>
          <div className="modal-backdrop fade show" style={{zIndex: 1040}}></div>
          <div className="modal fade show" style={{display: 'block', zIndex: 1050}} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-success text-white">
                  <h5 className="modal-title">Approve Loan</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowApprovalForm(false)}></button>
                </div>
              <form onSubmit={submitApproval}>
                <div className="modal-body">
                  <div className="alert alert-info">
                    <strong>Final Terms:</strong>
                    <ul className="mb-0 mt-2">
                      <li>Interest Rate: {selectedInterestRate}% per month</li>
                      <li>Monthly EMI: {formatCurrency(calculatedEMI)}</li>
                      <li>Total Amount: {formatCurrency(calculatedTotal)}</li>
                    </ul>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Remarks</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={approvalData.remarks}
                      onChange={(e) => setApprovalData({...approvalData, remarks: e.target.value})}
                      placeholder="Optional remarks..."
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Payment Method</label>
                    <select
                      className="form-select"
                      value={approvalData.paymentMethod}
                      onChange={(e) => setApprovalData({...approvalData, paymentMethod: e.target.value})}
                    >
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="bank_transfer">Bank Transfer</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Transaction ID (if applicable)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={approvalData.transactionId}
                      onChange={(e) => setApprovalData({...approvalData, transactionId: e.target.value})}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Preferred EMI Payment Date (1-31)</label>
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      max="31"
                      value={approvalData.preferredPaymentDate}
                      onChange={(e) => setApprovalData({...approvalData, preferredPaymentDate: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowApprovalForm(false)}>Cancel</button>
                  <button type="submit" className="btn btn-success" disabled={processing}>
                    {processing ? 'Processing...' : 'Confirm Approval'}
                  </button>
                </div>
              </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Rejection Modal */}
      {showRejectionForm && (
        <>
          <div className="modal-backdrop fade show" style={{zIndex: 1040}}></div>
          <div className="modal fade show" style={{display: 'block', zIndex: 1050}} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title">Reject Loan</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowRejectionForm(false)}></button>
                </div>
              <form onSubmit={submitRejection}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Rejection Reason *</label>
                    <select
                      className="form-select"
                      value={rejectionData.reason}
                      onChange={(e) => setRejectionData({...rejectionData, reason: e.target.value})}
                      required
                    >
                      <option value="">Select reason...</option>
                      <option value="low_credit_score">Low Credit Score</option>
                      <option value="insufficient_income">Insufficient Income</option>
                      <option value="document_verification_failed">Document Verification Failed</option>
                      <option value="location_verification_failed">Location Verification Failed</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Remarks</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={rejectionData.remarks}
                      onChange={(e) => setRejectionData({...rejectionData, remarks: e.target.value})}
                      placeholder="Provide detailed remarks..."
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Re-apply After (months)</label>
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      max="12"
                      value={rejectionData.reapplyAfter}
                      onChange={(e) => setRejectionData({...rejectionData, reapplyAfter: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowRejectionForm(false)}>Cancel</button>
                  <button type="submit" className="btn btn-danger" disabled={processing}>
                    {processing ? 'Processing...' : 'Confirm Rejection'}
                  </button>
                </div>
              </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LoanDetailPage;
